import { Flex, Stack, createStyles } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';

import { FieldSwitch } from '../../fields/FieldSwitch/FieldSwitch';
import type { NormalizedBaseSection } from '../../context/types/store';

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: 48,
    width: '100%',
    paddingLeft: theme.other.spacing._12,
    paddingRight: theme.other.spacing._12,
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[7]}`,
    textAlign: 'left',
  },
  info: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 3,
    cursor: 'not-allowed',
  },
  infoWrapper: {
    position: 'sticky',
  },
  wrapper: {
    position: 'absolute',
    zIndex: 2,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.gray2[1],
    opacity: 0.8,
    borderRadius: theme.radius.sm,
    cursor: 'not-allowed',
  },
  icon: {
    backgroundColor: theme.white,
    borderRadius: '50%',
    width: 64,
    height: 64,
  },
}));

export function BaseSectionComponent({ section }: { section: NormalizedBaseSection }) {
  const { classes } = useStyles();
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current && !section?.locked) return;

    const currentRef = ref.current;
    const handleResize = () => {
      const top = window.innerHeight / 2;
      currentRef.style.top = `${top}px`;
    };
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [section?.locked]);

  return (
    <Stack spacing="xl" pt={{ base: 'lg', sm: 'sm' }}>
      {section?.locked ? (
        <div className={classes.info}>
          <Flex
            className={classes.infoWrapper}
            align="center"
            ref={ref}
            justify="center"
            direction="column"
            /* eslint-disable-line local-rules/use-theme-tokens */ gap={24}
          >
            <Flex align="center" justify="center" className={classes.icon}>
              <IconLock size={24} />
            </Flex>
            <Title variant="headline-xl">{section.locked}</Title>
          </Flex>
        </div>
      ) : null}
      {section?.label && (
        <Title
          data-test-id={dt.section(section.key, 'label')}
          tag="h2"
          variant={{ base: 'subhead-medium', sm: 'subhead-large' }}
        >
          {section.label}
        </Title>
      )}
      {section?.description && (
        <Text data-test-id={dt.section(section.key, 'description')}>{section.description}</Text>
      )}

      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={32}>
        {section?.fields.map((fieldKey) => (
          <FieldSwitch fieldKey={fieldKey} key={fieldKey} sectionKey={section?.key ?? 'form'} />
        ))}
      </Stack>
      {section?.locked ? <div className={classes.wrapper} /> : null}
    </Stack>
  );
}
